/**
 * Job profitability viewer (WO-C9) + Time & Materials (accrue labor/materials against a job
 * and bill an invoice from actuals — no estimate required).
 *
 * There is no list-jobs endpoint, so this screen is a profitability viewer for a job id
 * the user navigates to (opaque UUID path param — no PII in URL). It fetches
 * GET /api/jobs/:id/profitability and renders the computed numbers defensively, then
 * embeds the reusable NotesPanel for contractor-only internal notes on the job.
 *
 * Profitability shape (beam-backend jobs.ts → phases.ts jobProfitability):
 *   { cost, contracted, margin, marginPct }
 *
 * Time & Materials: GET /api/jobs/:id/actuals previews unbilled labor + materials; "Log
 * hours" / "Log materials" post CreateTimeEntryRequest / CreateManualExpenseRequest (mirrors
 * the calendar screen's inline reveal-a-form pattern); "Bill time & materials" posts
 * /invoice-from-actuals and navigates to the created invoice. Disabled when there is
 * nothing to bill or some logged labor has no rate (hasUnratedLabor).
 *
 * Worker "Add update" (top of screen, above Profitability): a fast hours + what-did-you-do
 * (VoiceField) + optional materials form defaulting the worker to the signed-in user
 * (GET /api/auth/me). Feeds the SAME time-entries/expenses endpoints as the owner controls
 * below, so a tech's update shows up immediately in Time & Materials.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, me } from '../../../src/api/client';
import {
  getJobActuals,
  listLaborRates,
  createTimeEntry,
  createManualExpense,
  createInvoiceFromActuals,
} from '../../../src/api/jobs';
import { listUsers } from '../../../src/api/account';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { VoiceField } from '../../../src/components/voice/VoiceField';
import { NotesPanel } from '../../../src/components/NotesPanel';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';
import type { CreateTimeEntryRequest, CreateManualExpenseRequest } from '@beam/api-types';

// ---------------------------------------------------------------------------
// Types + client (local — single-use profitability shape)
// ---------------------------------------------------------------------------

interface JobProfitability {
  cost: number;
  contracted: number;
  margin: number;
  marginPct: number | null;
}

function getJobProfitability(jobId: string): Promise<JobProfitability> {
  return get<JobProfitability>(`/api/jobs/${jobId}/profitability`);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function StatRow({
  label,
  value,
  strong,
  color,
}: {
  label: string;
  value: string;
  strong?: boolean;
  color?: 'primary' | 'danger' | 'link';
}) {
  return (
    <View style={styles.cardRow}>
      <Text variant={strong ? 'bodyStrong' : 'small'} color={strong ? 'primary' : 'secondary'}>
        {label}
      </Text>
      <Text variant={strong ? 'amount' : 'data'} color={color}>
        {value}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Time & Materials — inline "Log hours" / "Log materials" forms
// (mirrors the calendar screen's inline reveal-a-form pattern)
// ---------------------------------------------------------------------------

const NO_RATE = '';
const NO_WORKER = '';

type TimeEntryFormValues = {
  worker_id: string;
  hours: number;
  labor_rate_id: string | null;
  notes: string | null;
};

function TimeEntryForm({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (values: TimeEntryFormValues) => void;
  onCancel: () => void;
}) {
  const [workerId, setWorkerId] = useState(NO_WORKER);
  const [hoursText, setHoursText] = useState('');
  const [laborRateId, setLaborRateId] = useState(NO_RATE);
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const usersQuery = useQuery({ queryKey: ['account-users'], queryFn: listUsers, staleTime: 60_000 });
  const ratesQuery = useQuery({ queryKey: ['labor-rates'], queryFn: listLaborRates, staleTime: 60_000 });

  const workerOptions = [
    { value: NO_WORKER, label: 'Select worker…' },
    ...(usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.full_name })),
  ];
  const rateOptions = [
    { value: NO_RATE, label: 'No rate' },
    ...(ratesQuery.data ?? []).map((r) => ({ value: r.id, label: r.name })),
  ];

  const submit = () => {
    setErr(null);
    if (!workerId) {
      setErr('Choose a worker.');
      return;
    }
    const hours = Number(hoursText);
    if (!hoursText.trim() || !Number.isFinite(hours) || hours <= 0) {
      setErr('Enter hours greater than 0.');
      return;
    }
    onSubmit({
      worker_id: workerId,
      hours,
      labor_rate_id: laborRateId || null,
      notes: notes.trim() ? notes.trim() : null,
    });
  };

  return (
    <Card>
      <Text variant="bodyStrong">Log hours</Text>
      <Select label="Worker" options={workerOptions} value={workerId} onChange={setWorkerId} />
      <Field
        label="Hours"
        placeholder="e.g. 2.5"
        value={hoursText}
        onChangeText={setHoursText}
        keyboardType="decimal-pad"
        accessibilityLabel="Hours worked"
      />
      <Select label="Labor rate" options={rateOptions} value={laborRateId} onChange={setLaborRateId} />
      <Field
        label="Notes (optional)"
        placeholder="What was done?"
        value={notes}
        onChangeText={setNotes}
        accessibilityLabel="Time entry notes"
      />
      {err ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {err}
        </Text>
      ) : null}
      <View style={styles.formActions}>
        <Button title="Log hours" onPress={submit} loading={busy} disabled={busy} testID="job-tm-submit-hours" />
        <Button title="Cancel" variant="secondary" onPress={onCancel} disabled={busy} />
      </View>
    </Card>
  );
}

type ManualExpenseFormValues = {
  description: string;
  total_amount: number;
};

function ManualExpenseForm({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (values: ManualExpenseFormValues) => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [amountText, setAmountText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    setErr(null);
    if (!description.trim()) {
      setErr('Enter a description.');
      return;
    }
    const amount = Number(amountText);
    if (!amountText.trim() || !Number.isFinite(amount) || amount < 0) {
      setErr('Enter a valid amount.');
      return;
    }
    onSubmit({ description: description.trim(), total_amount: amount });
  };

  return (
    <Card>
      <Text variant="bodyStrong">Log materials</Text>
      <Field
        label="Description"
        placeholder="e.g. Copper fittings"
        value={description}
        onChangeText={setDescription}
        accessibilityLabel="Material description"
      />
      <Field
        label="Amount"
        placeholder="0.00"
        value={amountText}
        onChangeText={setAmountText}
        keyboardType="decimal-pad"
        accessibilityLabel="Material amount"
      />
      {err ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {err}
        </Text>
      ) : null}
      <View style={styles.formActions}>
        <Button title="Log materials" onPress={submit} loading={busy} disabled={busy} testID="job-tm-submit-materials" />
        <Button title="Cancel" variant="secondary" onPress={onCancel} disabled={busy} />
      </View>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Worker "Add update" — a prominent, fast hours + what-did-you-do + optional
// materials form feeding the SAME T&M actuals as the owner "Log hours" / "Log
// materials" controls below. The worker defaults to the signed-in user (no
// worker picker), so a tech can record an update in one pass.
// ---------------------------------------------------------------------------

type AddUpdateValues = {
  hours: number;
  notes: string | null;
  materials: { description: string; total_amount: number } | null;
};

function AddUpdateForm({
  workerId,
  workerLoading,
  busy,
  onSubmit,
}: {
  workerId: string | undefined;
  workerLoading: boolean;
  busy: boolean;
  onSubmit: (values: AddUpdateValues) => void;
}) {
  const [hoursText, setHoursText] = useState('');
  const [note, setNote] = useState('');
  const [materialsDesc, setMaterialsDesc] = useState('');
  const [materialsAmountText, setMaterialsAmountText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    setErr(null);
    if (!workerId) {
      setErr('Still loading your profile. Try again in a moment.');
      return;
    }
    const hours = Number(hoursText);
    if (!hoursText.trim() || !Number.isFinite(hours) || hours <= 0) {
      setErr('Enter hours greater than 0.');
      return;
    }
    const descTrim = materialsDesc.trim();
    const amountTrim = materialsAmountText.trim();
    let materials: AddUpdateValues['materials'] = null;
    if (descTrim || amountTrim) {
      const amount = Number(amountTrim);
      if (!descTrim || !amountTrim || !Number.isFinite(amount) || amount < 0) {
        setErr('Enter both a material description and a valid amount, or leave both blank.');
        return;
      }
      materials = { description: descTrim, total_amount: amount };
    }

    onSubmit({ hours, notes: note.trim() ? note.trim() : null, materials });
    setHoursText('');
    setNote('');
    setMaterialsDesc('');
    setMaterialsAmountText('');
  };

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        Add update
      </Text>
      <Card>
        <Text variant="body" color="secondary">
          Log your hours and what you did. Add materials if you used any.
        </Text>
        <Field
          label="Hours"
          placeholder="e.g. 2.5"
          value={hoursText}
          onChangeText={setHoursText}
          keyboardType="decimal-pad"
          accessibilityLabel="Update hours"
        />
        <VoiceField
          label="What did you do?"
          placeholder="Describe the work"
          value={note}
          onChangeText={setNote}
          accessibilityLabel="What did you do"
        />
        <Field
          label="Material description (optional)"
          placeholder="e.g. Breaker"
          value={materialsDesc}
          onChangeText={setMaterialsDesc}
          accessibilityLabel="Update material description"
        />
        <Field
          label="Material amount (optional)"
          placeholder="0.00"
          value={materialsAmountText}
          onChangeText={setMaterialsAmountText}
          keyboardType="decimal-pad"
          accessibilityLabel="Update material amount"
        />
        {err ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {err}
          </Text>
        ) : null}
        <Button
          title="Add update"
          onPress={submit}
          loading={busy}
          disabled={busy || workerLoading}
          accessibilityLabel="Submit update"
          testID="job-add-update-submit"
        />
      </Card>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function JobDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['jobs', id, 'profitability'],
    queryFn: () => getJobProfitability(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Signed-in worker — the "Add update" form defaults to this user (no worker picker).
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me, staleTime: 60_000 });

  const [loggingHours, setLoggingHours] = useState(false);
  const [loggingMaterials, setLoggingMaterials] = useState(false);
  const [tmError, setTmError] = useState<string | null>(null);
  const [billError, setBillError] = useState<string | null>(null);

  const actualsQuery = useQuery({
    queryKey: ['jobs', id, 'actuals'],
    queryFn: () => getJobActuals(id!),
    enabled: !!id,
  });

  const timeEntryMutation = useMutation({
    mutationFn: (body: CreateTimeEntryRequest) => createTimeEntry(id!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', id, 'actuals'] });
      setLoggingHours(false);
      setTmError(null);
    },
    onError: (err) => setTmError(extractErrorMessage(err, 'Could not log hours.')),
  });

  const expenseMutation = useMutation({
    mutationFn: (body: CreateManualExpenseRequest) => createManualExpense(id!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', id, 'actuals'] });
      setLoggingMaterials(false);
      setTmError(null);
    },
    onError: (err) => setTmError(extractErrorMessage(err, 'Could not log materials.')),
  });

  const invoiceMutation = useMutation({
    mutationFn: () => createInvoiceFromActuals(id!),
    onSuccess: (res) => {
      setBillError(null);
      router.push(`/(app)/invoices/${res.invoiceId}`);
    },
    onError: (err) => setBillError(extractErrorMessage(err, 'Could not bill time and materials.')),
  });

  const handleAddUpdate = async (values: AddUpdateValues) => {
    if (!meQuery.data) return;
    try {
      await timeEntryMutation.mutateAsync({
        worker_id: meQuery.data.id,
        hours: values.hours,
        notes: values.notes,
        labor_rate_id: null,
      });
      if (values.materials) {
        await expenseMutation.mutateAsync(values.materials);
      }
    } catch {
      // Surfaced via tmError, already set by the mutations' onError above.
    }
  };

  const actuals = actualsQuery.data;
  const canBill = !!actuals && actuals.total > 0 && !actuals.hasUnratedLabor;

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No job ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  const marginPositive = data != null && data.margin >= 0;

  return (
    <Screen scroll>
      <Text variant="title">Job</Text>

      {/* Worker "Add update" — the most prominent action on this screen, above
          Profitability, so a tech can log hours/what-done/materials in one pass. */}
      <AddUpdateForm
        workerId={meQuery.data?.id}
        workerLoading={meQuery.isLoading}
        busy={timeEntryMutation.isPending || expenseMutation.isPending}
        onSubmit={handleAddUpdate}
      />

      <Card>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Job ID
          </Text>
          <Text variant="data">{id.slice(0, 8).toUpperCase()}</Text>
        </View>
      </Card>

      {/* Profitability */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Profitability
        </Text>
        <Card>
          {isLoading ? (
            <View style={styles.centeredInline}>
              <ActivityIndicator
                color={theme.color.action.primary}
                accessibilityLabel="Loading profitability"
              />
            </View>
          ) : isError ? (
            <View style={styles.errorBlock}>
              <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
                {extractErrorMessage(error, 'Failed to load profitability.')}
              </Text>
              <Button
                title="Retry"
                variant="secondary"
                size="sm"
                fullWidth={false}
                onPress={() => refetch()}
                accessibilityLabel="Retry loading profitability"
              />
            </View>
          ) : !data ? (
            <Text variant="body" color="secondary">
              No profitability data.
            </Text>
          ) : (
            <>
              <StatRow label="Contracted" value={formatMoney(data.contracted)} />
              <StatRow label="Cost" value={formatMoney(data.cost)} />
              <View style={styles.divider} />
              <StatRow
                label="Margin"
                value={formatMoney(data.margin)}
                strong
                color={marginPositive ? 'link' : 'danger'}
              />
              <StatRow
                label="Margin %"
                value={data.marginPct != null ? `${data.marginPct}%` : '—'}
              />
            </>
          )}
        </Card>
      </View>

      {/* Time & Materials — accrue labor + materials, then bill an invoice from actuals */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Time & Materials
        </Text>
        <Card>
          {actualsQuery.isLoading ? (
            <View style={styles.centeredInline}>
              <ActivityIndicator
                color={theme.color.action.primary}
                accessibilityLabel="Loading time and materials"
              />
            </View>
          ) : actualsQuery.isError ? (
            <View style={styles.errorBlock}>
              <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
                {extractErrorMessage(actualsQuery.error, 'Failed to load time and materials.')}
              </Text>
              <Button
                title="Retry"
                variant="secondary"
                size="sm"
                fullWidth={false}
                onPress={() => actualsQuery.refetch()}
                accessibilityLabel="Retry loading time and materials"
              />
            </View>
          ) : !actuals || (actuals.labor.length === 0 && actuals.materials.length === 0) ? (
            <Text variant="body" color="secondary">
              No hours or materials logged yet.
            </Text>
          ) : (
            <>
              {actuals.labor.map((l, i) => (
                <StatRow
                  key={`labor-${l.labor_rate_id ?? 'none'}-${i}`}
                  label={`${l.rate_name} · ${l.hours} hrs`}
                  value={formatMoney(l.amount)}
                />
              ))}
              {actuals.materials.map((m) => (
                <StatRow key={m.expense_id} label={m.description} value={formatMoney(m.amount)} />
              ))}
              <View style={styles.divider} />
              <StatRow label="Labor total" value={formatMoney(actuals.laborTotal)} />
              <StatRow label="Material total" value={formatMoney(actuals.materialTotal)} />
              <StatRow label="Total" value={formatMoney(actuals.total)} strong />
            </>
          )}
        </Card>

        {actuals?.hasUnratedLabor ? (
          <Text variant="small" color="danger">
            Some logged hours have no labor rate. Set a rate to bill.
          </Text>
        ) : null}

        {loggingHours ? (
          <TimeEntryForm
            busy={timeEntryMutation.isPending}
            onCancel={() => {
              setLoggingHours(false);
              setTmError(null);
            }}
            onSubmit={(v) =>
              timeEntryMutation.mutate({
                worker_id: v.worker_id,
                hours: v.hours,
                labor_rate_id: v.labor_rate_id,
                notes: v.notes,
              })
            }
          />
        ) : (
          <Button
            title="Log hours"
            variant="secondary"
            onPress={() => setLoggingHours(true)}
            accessibilityLabel="Log hours"
          />
        )}

        {loggingMaterials ? (
          <ManualExpenseForm
            busy={expenseMutation.isPending}
            onCancel={() => {
              setLoggingMaterials(false);
              setTmError(null);
            }}
            onSubmit={(v) =>
              expenseMutation.mutate({
                description: v.description,
                total_amount: v.total_amount,
              })
            }
          />
        ) : (
          <Button
            title="Log materials"
            variant="secondary"
            onPress={() => setLoggingMaterials(true)}
            accessibilityLabel="Log materials"
          />
        )}

        {tmError ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {tmError}
          </Text>
        ) : null}

        <Button
          title="Bill time & materials"
          onPress={() => invoiceMutation.mutate()}
          loading={invoiceMutation.isPending}
          disabled={invoiceMutation.isPending || !canBill}
          accessibilityLabel="Bill time and materials"
          testID="job-bill-tm"
        />

        {billError ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {billError}
          </Text>
        ) : null}
      </View>

      {/* Permits for this job */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">Permits</Text>
        <Card>
          <Button
            title="View job permits"
            variant="secondary"
            size="sm"
            fullWidth={false}
            testID="job-open-permits"
            onPress={() => router.push({ pathname: '/permits/by-job', params: { jobId: id } })}
            accessibilityLabel="View permits filed for this job"
          />
        </Card>
      </View>

      {/* Internal notes */}
      <NotesPanel ownerType="jobs" ownerId={id} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  centeredInline: {
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  section: {
    gap: theme.spacing.sm,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    marginVertical: theme.spacing.xs,
  },
  errorBlock: {
    gap: theme.spacing.sm,
  },
  formActions: {
    gap: theme.spacing.sm,
  },
});
